//#region IMPORTS
import { CodeBracketSquareIcon } from "@heroicons/react/24/outline";
import { fetchInstorePersoCountsAsync } from "../../features/instoreOrders/instoreOrdersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { InstoreOrder } from "../../models/instoreOrder";
import {
  buildPersoRows,
  exportPersoRowsToExcel,
} from "../../helpers/persoExcel";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useState } from "react";
import agent from "../../api/agent";
//#endregion

//#region INTERFACE
interface Props {
  instoreOrder: InstoreOrder;
}
//#endregion

// The perso cell of the instore orders list: does this order contain perso
// lines (order lines with custom_fields)? Same purple bracket icon as the
// perso column in the order detail.
//
// Where the answer comes from, in order:
//  1. `perso_lines_count` on the list row — the backend field we asked for; the
//     list does not carry it yet, so this is a no-op until it lands.
//  2. `state.instoreOrders.persoCounts[id]` — the client-side check: the "check"
//     button here (one order) or the perso check in the filter bar
//     (InstoreOrdersPersoCheck) fetches the detail and counts its perso lines.
// Until one of those has an answer the cell shows the "check" button.
//
// The "n perso" pill is itself the export button: it downloads this order's
// perso lines as Excel (helpers/persoExcel) — from the lines the check kept,
// or, when the count came from the backend, from a fresh detail fetch.
export default function InstoreOrdersTableRowActionPerso({
  instoreOrder,
}: Props) {
  //#region SETUP
  const dispatch = useAppDispatch();
  const checkedCount = useAppSelector(
    (state) => state.instoreOrders.persoCounts[instoreOrder.id],
  );
  const checkedRows = useAppSelector(
    (state) => state.instoreOrders.persoLines[instoreOrder.id],
  );
  const checking = useAppSelector((state) =>
    state.instoreOrders.persoCheckingIds.includes(instoreOrder.id),
  );
  const [exporting, setExporting] = useState(false);
  const count = instoreOrder.perso_lines_count ?? checkedCount;
  //#endregion

  //#region LOGIC
  const exportPerso = async () => {
    setExporting(true);
    try {
      const rows =
        checkedRows ??
        buildPersoRows((await agent.InstoreOrders.detail(instoreOrder.id)).data);
      if (rows.length === 0) {
        toast.info("No perso lines in this order");
        return;
      }
      exportPersoRowsToExcel(
        rows,
        `perso_${instoreOrder.order_code}_${format(new Date(), "dd-MM-yyyy")}.xlsx`,
      );
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      setExporting(false);
    }
  };
  //#endregion

  //#region RENDER
  if (checking) {
    return (
      <span className="inline-flex items-center gap-x-1 text-xs text-purple-700 animate-pulse">
        <CodeBracketSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
        checking…
      </span>
    );
  }

  if (count === undefined) {
    return (
      <button
        type="button"
        onClick={() => dispatch(fetchInstorePersoCountsAsync([instoreOrder.id]))}
        title="Check whether this order contains perso"
        className="inline-flex items-center gap-x-1 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-300 hover:bg-purple-50 hover:text-purple-900 hover:ring-purple-300"
      >
        <CodeBracketSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
        check
      </button>
    );
  }

  if (count === 0) {
    return <span className="text-xs text-gray-500">no perso</span>;
  }

  return (
    <button
      type="button"
      onClick={exportPerso}
      disabled={exporting}
      title="Export this order's perso lines to Excel"
      className="inline-flex items-center gap-x-1 rounded-full bg-purple-200 px-2 py-0.5 text-xs font-medium text-purple-900 hover:bg-purple-700 hover:text-white disabled:cursor-wait disabled:opacity-60"
    >
      <CodeBracketSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {/* Count prepended, not passed to t(): "perso" is jargon and stays
          untranslated anyway (see the i18n notes in CLAUDE.md). */}
      <span className="font-bold">{count}</span>
      perso
    </button>
  );
  //#endregion
}
